// @ts-check
/**
 * Admin service for the Atelier B2B side.
 * All calls go through the centralized apiClient (auth + 401 handling).
 */
import api from '../utils/apiClient';

const B = '/api/admin/atelier';

const atelierAdminService = {
  // ── Trade accounts ─────────────────────────────────────────
  listAccounts(status) {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return api.get(`${B}/accounts/${q}`);
  },
  getAccount(id) {
    return api.get(`${B}/accounts/${id}`);
  },
  getAccountDocuments(id) {
    return api.get(`${B}/accounts/${id}/documents`);
  },
  approveAccount(id) {
    return api.post(`${B}/accounts/${id}/approve`);
  },
  rejectAccount(id, reason) {
    return api.post(`${B}/accounts/${id}/reject`, { reason });
  },
  suspendAccount(id) {
    return api.post(`${B}/accounts/${id}/suspend`);
  },

  // ── NDA ────────────────────────────────────────────────────
  getNda(accountId) {
    return api.get(`${B}/accounts/${accountId}/nda`);
  },
  generateNda(accountId) {
    return api.post(`${B}/accounts/${accountId}/nda/generate`);
  },
  sendNda(accountId, payload) {
    return api.post(`${B}/accounts/${accountId}/nda/send`, payload);
  },
  markNdaSigned(accountId) {
    return api.post(`${B}/accounts/${accountId}/nda/mark-signed`);
  },

  // ── Projects ───────────────────────────────────────────────
  /** @param {{ stage?: string, accountId?: string }} [filters] */
  listProjects({ stage, accountId } = {}) {
    const q = new URLSearchParams();
    if (stage) q.append('stage', stage);
    if (accountId) q.append('account_id', accountId);
    const qs = q.toString();
    return api.get(`${B}/projects/${qs ? `?${qs}` : ''}`);
  },
  getProject(id) {
    return api.get(`${B}/projects/${id}`);
  },
  getTimeline(id) {
    return api.get(`${B}/projects/${id}/timeline`);
  },
  advanceStage(id, toStage, remark) {
    return api.post(`${B}/projects/${id}/stage`, { to_stage: toStage, remark });
  },
  editProject(id, data) {
    return api.patch(`${B}/projects/${id}`, data);
  },
  getProjectNotes(id) {
    return api.get(`${B}/projects/${id}/notes`);
  },
  setProjectNotes(id, notes) {
    return api.put(`${B}/projects/${id}/notes`, { notes });
  },
  getAccountNotes(id) {
    return api.get(`${B}/accounts/${id}/notes`);
  },
  setAccountNotes(id, notes) {
    return api.put(`${B}/accounts/${id}/notes`, { notes });
  },

  // ── Stage reasons ──────────────────────────────────────────
  listStageReasons(toStage) {
    const q = toStage ? `?to_stage=${encodeURIComponent(toStage)}&include_inactive=true` : '?include_inactive=true';
    return api.get(`${B}/stage-reasons/${q}`);
  },
  createStageReason(data) {
    return api.post(`${B}/stage-reasons/`, data);
  },
  updateStageReason(id, data) {
    return api.patch(`${B}/stage-reasons/${id}`, data);
  },
  deleteStageReason(id) {
    return api.del(`${B}/stage-reasons/${id}`);
  },

  // ── Account rejection reasons ──────────────────────────────
  listAccountRejectionReasons() {
    return api.get(`${B}/account-rejection-reasons/?include_inactive=true`);
  },
  createAccountRejectionReason(data) {
    return api.post(`${B}/account-rejection-reasons/`, data);
  },
  updateAccountRejectionReason(id, data) {
    return api.patch(`${B}/account-rejection-reasons/${id}`, data);
  },
  deleteAccountRejectionReason(id) {
    return api.del(`${B}/account-rejection-reasons/${id}`);
  },

  // ── Pricing / quotes ───────────────────────────────────────
  quotePreview(projectId, payload) {
    return api.post(`${B}/projects/${projectId}/quote-preview`, payload);
  },
  setQuote(projectId, payload) {
    return api.post(`${B}/projects/${projectId}/quote`, payload);
  },

  // ── Materials / BOM costing ────────────────────────────────
  listProjectMaterials(projectId) {
    return api.get(`${B}/projects/${projectId}/materials`);
  },
  getProjectMaterialsSummary(projectId) {
    return api.get(`${B}/projects/${projectId}/materials/summary`);
  },
  addProjectMaterial(projectId, data) {
    return api.post(`${B}/projects/${projectId}/materials`, data);
  },
  updateProjectMaterial(projectId, lineId, data) {
    return api.put(`${B}/projects/${projectId}/materials/${lineId}`, data);
  },
  deleteProjectMaterial(projectId, lineId) {
    return api.del(`${B}/projects/${projectId}/materials/${lineId}`);
  },
  copyMaterialsFromProduct(projectId, variantId) {
    return api.post(`${B}/projects/${projectId}/materials/copy-from-product?variant_id=${variantId}`);
  },

  // ── Wholesale prices ───────────────────────────────────────
  listWholesale() {
    return api.get(`${B}/wholesale-prices/`);
  },
  createWholesale(data) {
    return api.post(`${B}/wholesale-prices/`, data);
  },
  updateWholesale(id, data) {
    return api.patch(`${B}/wholesale-prices/${id}`, data);
  },
  deleteWholesale(id) {
    return api.del(`${B}/wholesale-prices/${id}`);
  },

  // ── Invoices ───────────────────────────────────────────────
  listProjectInvoices(projectId) {
    return api.get(`${B}/projects/${projectId}/invoices`);
  },
  createInvoice(projectId, milestone) {
    return api.post(`${B}/projects/${projectId}/invoices?milestone=${milestone}`);
  },
  generate5050(projectId) {
    return api.post(`${B}/projects/${projectId}/invoices/generate-5050`);
  },
  markInvoicePaid(invoiceId, paymentMethod) {
    return api.post(`${B}/invoices/${invoiceId}/mark-paid`, { payment_method: paymentMethod });
  },

  // ── Messages ───────────────────────────────────────────────
  listMessages(projectId) {
    return api.get(`${B}/projects/${projectId}/messages`);
  },
  postMessage(projectId, body, attachments = []) {
    return api.post(`${B}/projects/${projectId}/messages`, { body, attachments });
  },
  unreadByProject() {
    return api.get(`${B}/projects/messages/unread`);
  },

  // ── Attachments ────────────────────────────────────────────
  listAttachments(projectId) {
    return api.get(`${B}/projects/${projectId}/attachments`);
  },
  uploadAttachment(projectId, kind, file) {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    return api.upload(`${B}/projects/${projectId}/attachments`, fd);
  },
  deleteAttachment(attachmentId) {
    return api.del(`${B}/projects/attachments/${attachmentId}`);
  },

  // ── Settings ───────────────────────────────────────────────
  getBankDetails() {
    return api.get(`${B}/settings/bank-details`);
  },
  setBankDetails(value) {
    return api.put(`${B}/settings/bank-details`, { value });
  },
  getContent() {
    return api.get(`${B}/settings/content`);
  },
  setContent(data) {
    return api.put(`${B}/settings/content`, data);
  },

  // ── Outreach CRM ───────────────────────────────────────────
  /** @param {{ status?: string, search?: string }} [filters] */
  listProspects({ status, search } = {}) {
    const q = new URLSearchParams();
    if (status) q.append('status', status);
    if (search) q.append('search', search);
    const qs = q.toString();
    return api.get(`${B}/prospects/${qs ? `?${qs}` : ''}`);
  },
  getProspect(id) {
    return api.get(`${B}/prospects/${id}`);
  },
  createProspect(data) {
    return api.post(`${B}/prospects/`, data);
  },
  importProspects(prospects) {
    return api.post(`${B}/prospects/import`, { prospects });
  },
  updateProspect(id, data) {
    return api.patch(`${B}/prospects/${id}`, data);
  },
  deleteProspect(id) {
    return api.del(`${B}/prospects/${id}`);
  },
  logOutreach(id, data) {
    return api.post(`${B}/prospects/${id}/outreach`, data);
  },
  convertProspect(id) {
    return api.post(`${B}/prospects/${id}/convert`);
  },
  crmStats() {
    return api.get(`${B}/prospects/stats`);
  },

  // ── Templates ──────────────────────────────────────────────
  listTemplates(type) {
    const q = type ? `?template_type=${encodeURIComponent(type)}` : '';
    return api.get(`${B}/templates/${q}`);
  },
  updateTemplate(id, data) {
    return api.patch(`${B}/templates/${id}`, data);
  },
  createTemplate(data) {
    return api.post(`${B}/templates/`, data);
  },
};

// Stage lifecycle order + labels — mirrors backend AtelierProjectStage.
export const STAGES = [
  'submitted',
  'reviewing',
  'nda_pending',
  'sample_dev',
  'sample_shipped',
  'quoted',
  'approved',
  'deposit_paid',
  'in_production',
  'qc',
  'balance_paid',
  'shipped',
  'delivered',
  'cancelled',
];

export const STAGE_LABELS = {
  submitted: 'Submitted',
  reviewing: 'Under Review',
  nda_pending: 'NDA Pending',
  sample_dev: 'Sample Dev',
  sample_shipped: 'Sample Shipped',
  quoted: 'Quoted',
  approved: 'Approved',
  deposit_paid: 'Deposit Paid',
  in_production: 'In Production',
  qc: 'Quality Control',
  balance_paid: 'Balance Paid',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export const PROSPECT_STATUSES = ['new', 'contacted', 'replied', 'meeting', 'negotiating', 'converted', 'declined'];
export const PROSPECT_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  replied: 'Replied',
  meeting: 'Meeting',
  negotiating: 'Negotiating',
  converted: 'Converted',
  declined: 'Declined',
};

export default atelierAdminService;
